import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  inject,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Lesson } from 'core/models/course-content.model';
import { LessonHeader } from 'components/lesson-header/lesson-header';

@Component({
  selector: 'app-video-preview',
  standalone: true,
  imports: [CommonModule, LessonHeader],
  templateUrl: './video-preview.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'w-full h-full flex flex-col',
  },
})
export class VideoPreview {
  private readonly sanitizer = inject(DomSanitizer);

  @Input({ required: true }) lesson!: Lesson;

  protected get safeVideoUrl(): SafeResourceUrl | null {
    if (!this.lesson?.videoUrl) {
      return null;
    }

    // Validar se a URL parece ser uma URL válida de embed
    const url = this.lesson.videoUrl.trim();

    // Verificar se é uma URL válida de embed do YouTube ou Vimeo
    const isValidEmbedUrl =
      url.includes('youtube.com/embed/') ||
      url.includes('youtu.be/') ||
      url.includes('vimeo.com/video/') ||
      url.includes('player.vimeo.com/video/');

    // Se não for uma URL válida de embed, retornar null para exibir mensagem de erro
    if (!isValidEmbedUrl && !url.startsWith('http')) {
      return null;
    }

    try {
      return this.sanitizer.bypassSecurityTrustResourceUrl(url);
    } catch (error) {
      // Se houver erro ao sanitizar, retornar null
      console.error('Erro ao sanitizar URL do vídeo:', error);
      return null;
    }
  }

  protected get youtubeWatchUrl(): string | null {
    if (!this.lesson?.videoUrl) {
      return null;
    }

    if (this.lesson.videoUrl.includes('youtube.com/watch')) {
      return this.lesson.videoUrl;
    }

    const embedMatch = this.lesson.videoUrl.match(
      /youtube\.com\/embed\/([a-zA-Z0-9_-]+)/,
    );
    if (embedMatch && embedMatch[1]) {
      return `https://www.youtube.com/watch?v=${embedMatch[1]}`;
    }

    const shortMatch = this.lesson.videoUrl.match(
      /youtu\.be\/([a-zA-Z0-9_-]+)/,
    );
    if (shortMatch && shortMatch[1]) {
      return `https://www.youtube.com/watch?v=${shortMatch[1]}`;
    }

    return this.lesson.videoUrl;
  }
}
